import { HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { inject } from '@angular/core';
import {
  ApolloClient,
  ApolloLink,
  CombinedGraphQLErrors,
  DefaultOptions,
  FetchResult,
  InMemoryCache,
} from '@apollo/client/core';
import { ErrorLink } from '@apollo/client/link/error';
import { EnvironmentService } from '@util/environment';
import { HttpLink } from 'apollo-angular/http';
import UploadHttpLink from 'apollo-upload-client/UploadHttpLink.mjs';
import { GraphQLFormattedError, OperationTypeNode } from 'graphql';
import { MonoTypeOperatorFunction, Observable, catchError, map, mergeMap, of, switchMap, tap } from 'rxjs';
import { ErrorParam, Error as GqlErrorResponse } from './generated/gql.model';
import { isUnauthorizedError } from './graphql-error.util';
import { GraphqlService } from './graphql.service';
import { GraphqlError } from './helpers/errors';

const ERROR_TYPENAME: GqlErrorResponse['__typename'] = 'Error';

export function apolloProvider(): ApolloClient.Options {
  const httpLink: HttpLink = inject(HttpLink);
  const graphqlService: GraphqlService = inject(GraphqlService);
  const env: EnvironmentService = inject(EnvironmentService);

  const uri: string = `${env.environment.api.graphqlGateway}/graphql`;
  const middleware: ApolloLink = createAuthorizationLink(() => graphqlService.token$());
  const cache: InMemoryCache = new InMemoryCache();
  const defaultOptions: DefaultOptions = { query: { fetchPolicy: 'no-cache' } };
  const graphqlHttpLink: ApolloLink = httpLink.create({ uri });
  const uploadLink: ApolloLink = new UploadHttpLink({ uri });
  const apolloLink: ApolloLink = ApolloLink.split(
    (operation: ApolloLink.Operation) => operation.getContext()['useMultipart'],
    middleware.concat(uploadLink),
    middleware.concat(graphqlHttpLink),
  );

  const networkErrorLink: ApolloLink = new ErrorLink(({ error }: ErrorLink.ErrorHandlerOptions): void => {
    if (CombinedGraphQLErrors.is(error)) {
      error.errors.forEach((gqlError: GraphQLFormattedError) => {
        throw new GraphqlError(gqlError.message);
      });
    }
    if (error instanceof HttpErrorResponse) {
      const message: string = error.error?.message;
      throw new GraphqlError(message);
    }
  });

  // Custom response handling as a link: https://www.apollographql.com/docs/react/api/link/introduction/#handling-a-response
  const schemaErrorLink: ApolloLink = new ApolloLink(
    (operation: ApolloLink.Operation, forward: ApolloLink.ForwardFunction): Observable<FetchResult> =>
      forward(operation).pipe(
        map((data: FetchResult): FetchResult => {
          if (data.errors?.length > 0) {
            data.errors.forEach((error: GraphQLFormattedError) => {
              throw new GraphqlError(error.message);
            });
          }

          // TODO: Do we still want to keep this, since BE should always return GraphQLError?
          Object.keys(data.data)
            .filter((key: string) => data.data[key]?.__typename === ERROR_TYPENAME)
            .forEach((key: string) => {
              const message: string = data.data[key].message;
              const params: object = data.data[key].params ? mapErrorParamArrayToObject(data.data[key].params) : {};
              throw new GraphqlError(message, params);
            });
          return data;
        }),
      ),
  );

  const link: ApolloLink = ApolloLink.from([
    networkErrorLink,
    schemaErrorLink,
    createUnauthorizedReplayLink(graphqlService),
    apolloLink,
  ]);
  return { link, cache, defaultOptions };
}

export function createAuthorizationLink(token$: () => Observable<string | null>): ApolloLink {
  return new ApolloLink((operation: ApolloLink.Operation, forward: ApolloLink.ForwardFunction) =>
    token$().pipe(
      mergeMap((token: string | null) => {
        if (token) {
          setAuthorizationHeader(operation, `Bearer ${token}`);
        }
        return forward(operation);
      }),
    ),
  );
}

export function createUnauthorizedReplayLink(graphqlService: GraphqlService): ApolloLink {
  return new ApolloLink((operation: ApolloLink.Operation, forward: ApolloLink.ForwardFunction) =>
    forward(operation).pipe(
      mergeMap((result: FetchResult): Observable<FetchResult> => {
        if (!hasUnauthorizedError(result)) {
          return of(result);
        }
        return replayAfterRefresh$(graphqlService, operation, forward, result);
      }),
    ),
  );
}

function replayAfterRefresh$(
  graphqlService: GraphqlService,
  operation: ApolloLink.Operation,
  forward: ApolloLink.ForwardFunction,
  unauthorizedResult: FetchResult,
): Observable<FetchResult> {
  return graphqlService.refreshAfterUnauthorized$().pipe(
    map((): boolean => true),
    catchError((error: unknown): Observable<boolean> => {
      if (graphqlService.isSessionRejected(error)) {
        graphqlService.logout();
      }
      return of(false);
    }),
    switchMap(
      (refreshed: boolean): Observable<FetchResult> =>
        refreshed && operation.operationType === OperationTypeNode.QUERY
          ? forward(operation).pipe(logoutOnUnauthorized(graphqlService))
          : of(unauthorizedResult),
    ),
  );
}

function logoutOnUnauthorized(graphqlService: GraphqlService): MonoTypeOperatorFunction<FetchResult> {
  return tap((result: FetchResult): void => {
    if (hasUnauthorizedError(result)) {
      graphqlService.logout();
    }
  });
}

function hasUnauthorizedError(result: FetchResult): boolean {
  return (result.errors ?? []).some(isUnauthorizedError);
}

function setAuthorizationHeader(operation: ApolloLink.Operation, authorizationHeader: string): void {
  const useMultipart: boolean = !!operation.getContext()['useMultipart'];
  // Using an instance of HttpHeaders when using the Apollo uploadLink does not work
  if (!useMultipart) {
    operation.setContext({
      headers: new HttpHeaders().set('Authorization', authorizationHeader),
    });
  } else {
    operation.setContext({
      headers: { Authorization: authorizationHeader },
    });
  }
}

export function mapErrorParamArrayToObject(params: ErrorParam[]): object {
  return params.reduce(
    (paramObject: object, param: ErrorParam) => ({ ...paramObject, [param.param]: param.value }),
    {},
  );
}
